import { useState, useEffect, useContext } from "react";
import { useApi } from "hooks";
import useDataTable from "./useDataTable";
import { StationContext } from "contexts";

const ALL_ITEMS_PER_PAGE = 1000;

// Custom hook for handling selectable items using modal
const useSelectableItems = ({
  active,
  uniqueKey,
  allItems = [],
  onFetch = () => {},
  value = [],
  isCheckAll = false,
  onFetchAllItemMapFilterState = (filterState) => filterState,
  dataTable: { api: dataTableApi, table, filter },
}) => {
  const { station } = useContext(StationContext);
  const [selected, setSelected] = useState(value);
  const [allItemsState, setAllItemsState] = useState(allItems);
  const [loading, setLoading] = useState(false);
  const [isCheckAllState, setIsCheckAllState] = useState(isCheckAll);

  const getAllItemKeys = (items) => items.map((item) => item?.[uniqueKey]);

  const onCheck = (id) => {
    setSelected((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id],
    );
  };

  const handleOnCheckAll = () => {
    const allItemKeys = getAllItemKeys(allItemsState);

    setSelected((prevSelected) =>
      isCheckAllState
        ? prevSelected.filter((item) => !allItemKeys.includes(item))
        : [...new Set([...prevSelected, ...allItemKeys])],
    );
    setIsCheckAllState(!isCheckAllState);
  };

  const dataTable = useDataTable({
    api: {
      ...dataTableApi,
      params: {
        ...dataTableApi?.params,
        stationCodes: station.stationCode,
      },
    },
    filter: filter,
    table: {
      ...table,
      mapper: (item) =>
        table?.mapper(item, {
          onCheck,
          selected,
          isCheckAllState,
          setIsCheckAllState,
          allItemsState,
        }),
    },
  });

  const { mapper: mapFilterToRequest, initialState: initialFilterState } = filter;

  const getAllItems = useApi({ api: dataTableApi?.api });

  const fetchAllItems = async (filterState) => {
    setLoading(true);
    try {
      const res = await getAllItems?.request(
        {
          ...mapFilterToRequest({
            ...(onFetchAllItemMapFilterState?.(filterState) || filterState),
          }),
          perPage: ALL_ITEMS_PER_PAGE,
          stationCodes: station.stationCode,
        },
        () => {},
        { useLoader: true },
      );

      const fetchedItems = res?.[table?.key] ?? [];

      if (isCheckAllState) {
        setSelected((itemIds) => [...new Set([...itemIds, ...getAllItemKeys(fetchedItems)])]);
      }
      onFetch?.(fetchedItems);
      return fetchedItems;
    } catch (error) {
      // Handle error accordingly
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const refetchAllItems = async (filterState, searchKey) => {
    const fetchedItems = await fetchAllItems({ ...filterState, searchKey });

    // keep the previous items when the request failed
    if (fetchedItems) {
      setAllItemsState(fetchedItems);
    }
  };

  useEffect(() => {
    // an empty list is a valid state, e.g. while the items are still loading
    if (active && allItems.length > 0) {
      setAllItemsState(allItems);
    }
  }, [active, allItems]);

  // reset the selection every time the modal opens. `value` is intentionally not a dependency,
  // callers usually build it on every render and it would overwrite the user's selection.
  useEffect(() => {
    if (active) {
      setSelected(value);
      setIsCheckAllState(false);
      dataTable.search?.modifySearch("");
      dataTable.filter.applyClearFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const applyClearSearch = async () => {
    await refetchAllItems(dataTable.filter.filterState, "");
    dataTable.search.applyClearSearch();
  };

  const applyFilter = async () => {
    await refetchAllItems(dataTable.filter.filterState, "");
    dataTable.filter?.applyFilter();
  };

  const applySearch = async () => {
    await refetchAllItems(dataTable.filter.filterState, dataTable.search.searchKey);
    dataTable.search.applySearch();
  };

  const applyClearFilter = async () => {
    await refetchAllItems(initialFilterState, "");
    dataTable.search.modifySearch("");
    dataTable.filter.applyClearFilter();
  };

  return {
    allItemsState,
    selected,
    setSelected,
    loading,
    fetchAllItems,
    handleOnCheckAll,
    onCheck,
    setIsCheckAllState,
    isCheckAllState,
    getAllItems,
    dataTable: {
      ...dataTable,
      filter: {
        ...dataTable.filter,
        applyClearFilter,
        applyFilter,
      },
      search: {
        ...dataTable.search,
        applySearch,
        applyClearSearch,
      },
    },
  };
};

export default useSelectableItems;
